import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function toDate(d) {
  return d ? new Date(d).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
}

function isoDay(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

export default function AttendancePage() {
  const [start, setStart] = useState(isoDay(-6));
  const [end, setEnd] = useState(isoDay(0));
  const [empId, setEmpId] = useState("all");
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/employees").then(({ data }) => setEmployees(data));
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = { start_date: start, end_date: end };
        if (empId && empId !== "all") params.employee_id = empId;
        const { data } = await api.get("/attendance", { params });
        setRecords(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [start, end, empId]);

  const totalHours = useMemo(
    () => records.reduce((s, r) => s + (r.hours || 0), 0).toFixed(2),
    [records]
  );

  return (
    <div data-testid="attendance-page">
      <div className="fade-up">
        <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">History</p>
        <h1 className="font-serif text-3xl sm:text-5xl tracking-tight mt-2">Attendance</h1>
        <p className="mt-3 text-muted-foreground">Filter by date range and employee.</p>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-4 bg-white border border-border p-6 fade-up">
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">From</Label>
          <Input type="date" data-testid="filter-start" value={start} onChange={(e) => setStart(e.target.value)} className="mt-2" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">To</Label>
          <Input type="date" data-testid="filter-end" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-2" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Employee</Label>
          <Select value={empId} onValueChange={setEmpId}>
            <SelectTrigger className="mt-2" data-testid="filter-employee">
              <SelectValue placeholder="All employees" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All employees</SelectItem>
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-6 flex items-baseline justify-between">
        <span className="text-sm text-muted-foreground" data-testid="record-count">
          {records.length} records
        </span>
        <span className="text-sm">
          Total hours: <span className="font-serif text-xl" data-testid="total-hours">{totalHours}</span>
        </span>
      </div>

      <div className="mt-4 bg-white border border-border fade-up">
        {loading ? (
          <div className="p-10 text-center text-muted-foreground">Loading…</div>
        ) : records.length === 0 ? (
          <div className="p-14 text-center">
            <p className="font-serif text-2xl">No records in this range.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[560px]" data-testid="attendance-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                <th className="px-6 py-4 font-normal">Date</th>
                <th className="px-6 py-4 font-normal">Employee</th>
                <th className="px-6 py-4 font-normal">Check in</th>
                <th className="px-6 py-4 font-normal">Check out</th>
                <th className="px-6 py-4 font-normal text-right">Hours</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-6 py-4">{r.date}</td>
                  <td className="px-6 py-4 font-medium">{r.employee_name}</td>
                  <td className="px-6 py-4 text-muted-foreground">{toDate(r.check_in)}</td>
                  <td className="px-6 py-4 text-muted-foreground">{r.check_out ? toDate(r.check_out) : <span className="text-primary">On shift</span>}</td>
                  <td className="px-6 py-4 text-right font-serif text-lg">{r.hours || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
